/** Fortify Privacy Policy */

import { GENERAL_INQUIRIES_EMAIL, PRIVACY_EMAIL } from "lib/fortifyContact";

export const privacyPolicyMeta = {
  company: "FORTIFY PORTAL, INC.",
  title: "Privacy Policy",
  contactLine: `fortifyportal.ca | ${GENERAL_INQUIRIES_EMAIL}`,
};

export const privacyPolicyIntro =
  'Fortify Portal, Inc. ("Fortify", "we", "us", or "our") is committed to protecting the privacy of its users. This Privacy Policy describes how we collect, use, disclose, and safeguard personal information in accordance with the Personal Information Protection and Electronic Documents Act (Canada) ("PIPEDA") and other applicable privacy legislation.';

export const privacyPolicySections = [
  {
    number: "1",
    title: "Information We Collect",
    labeledItems: [
      {
        label: "Identity Information",
        text: "Full legal name, date of birth.",
      },
      {
        label: "Contact Information",
        text: "Email address, mailing address, and phone number.",
      },
      {
        label: "Financial Information",
        text: "Bank account details for processing investment transactions.",
      },
      {
        label: "Usage Information",
        text: "IP address, browser type, pages visited, and other technical information collected through cookies and similar technologies.",
      },
      {
        label: "Investment Activity",
        text: "Records of investments made through the Platform, including amounts, dates, and issuer details.",
      },
    ],
  },
  {
    number: "2",
    title: "How We Use Your Information",
    paragraphs: ["We use your personal information to:"],
    listItems: [
      "Verify your identity and eligibility to use the Platform;",
      "Process and record investment transactions;",
      "Comply with applicable securities laws, including NI 45-110;",
      "Communicate with you about your account and Platform updates; and",
      "Improve the Platform and user experience.",
    ],
    paragraphsAfterList: [
      "We collect, use, and disclose your personal information with your consent or as otherwise permitted or required by law. Where required, we obtain your express consent for the collection of sensitive information, such as your identity and financial details. You may withdraw your consent at any time, subject to legal, regulatory, and contractual restrictions; withdrawing consent may prevent you from continuing to use the Platform, and we will continue to retain any information we are required by law to keep.",
    ],
  },
  {
    number: "3",
    title: "Disclosure of Your Information",
    paragraphs: ["We may share your personal information with:"],
    listItems: [
      "Issuers, to the extent required to complete an investment transaction;",
      "Payment processors and financial institutions, for the purpose of processing transactions;",
      "Regulatory authorities, including provincial securities commissions, as required by law; and",
      "Service providers who assist us in operating the Platform, subject to confidentiality obligations.",
    ],
    paragraphsAfterList: [
      "We do not sell your personal information to third parties. We store and process personal information in Canada and, where our service providers operate outside Canada, your information may be transferred to and stored in those jurisdictions, where it may be subject to their laws. In all cases we use contractual and other measures to protect your personal information and remain accountable for it.",
    ],
  },
  {
    number: "4",
    title: "Email Communications",
    subsections: [
      {
        title: "Service and Legal Notices",
        paragraphs: [
          "We use the email address associated with your account to send notices required to operate the Platform or required by law, including subscription confirmations, refund notices, amendment notifications, and regulatory disclosures. You cannot opt out of these notices while you have an active account or open subscription.",
        ],
      },
      {
        title: "Promotional Communications",
        paragraphs: [
          `From time to time, Fortify may send promotional communications. You may opt out at any time by clicking "Unsubscribe" or by contacting us at ${GENERAL_INQUIRIES_EMAIL}. Opting out does not affect your receipt of service and legal notices.`,
        ],
      },
    ],
  },
  {
    number: "5",
    title: "Your Rights",
    paragraphs: [
      `You have the right to request access to, and correction of, the personal information we hold about you. To make a request, contact our Privacy Officer at ${PRIVACY_EMAIL}.`,
    ],
  },
  {
    number: "6",
    title: "Data Retention",
    paragraphs: [
      "We retain personal information for as long as necessary to fulfill the purposes for which it was collected. Records we are required to keep under NI 45-110, including records of client transactions and of our compliance with the instrument, are retained for at least eight (8) years from the date the record is created, as required by NI 45-110 and applicable law.",
    ],
  },
  {
    number: "7",
    title: "Security",
    paragraphs: [
      "We implement reasonable administrative, technical, and physical safeguards to protect your personal information, including secure storage and access controls. These safeguards apply to investor data, transaction records, identity-verification records, offering documents, and audit logs.",
    ],
  },
  {
    number: "8",
    title: "Cookies",
    paragraphs: [
      "We use cookies and similar tracking technologies to enhance your experience on the Platform. You may disable cookies through your browser settings, though doing so may affect Platform functionality.",
    ],
  },
  {
    number: "9",
    title: "Amendments",
    paragraphs: [
      "We may update this Privacy Policy from time to time. Material amendments will be posted on the Platform. Your continued use of the Platform after an amendment constitutes acceptance of the amended Policy.",
    ],
  },
  {
    number: "10",
    title: "Contact",
    paragraphs: ["For privacy-related inquiries, contact our Privacy Officer at:"],
    contactLines: [
      {
        label: "Email:",
        value: PRIVACY_EMAIL,
        href: `mailto:${PRIVACY_EMAIL}`,
      },
    ],
    paragraphsAfterContact: [
      "You also have the right to file a complaint with the Office of the Privacy Commissioner of Canada at www.priv.gc.ca.",
    ],
  },
];
